// src/routes/Admin/ChatHistoryManagement.js

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Collapse,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { statusBg, adaptiveColor, border } from '../../utils/darkModeAdapter';
import axios from 'axios';

/** ユーザー/AIメッセージのスタイル（テーマ対応） */
const getUserMessageStyle = (theme) => ({
  alignSelf: 'flex-start',
  backgroundColor: statusBg(theme).info,
  padding: '8px 12px',
  borderRadius: '12px',
  maxWidth: '70%',
  marginBottom: '8px',
  whiteSpace: 'pre-wrap',
});

const getAssistantMessageStyle = (theme) => ({
  alignSelf: 'flex-end',
  backgroundColor: adaptiveColor(theme, '#fff9c4', 'rgba(255,193,7,0.12)'),
  padding: '8px 12px',
  borderRadius: '12px',
  maxWidth: '70%',
  marginBottom: '8px',
  whiteSpace: 'pre-wrap',
});

/** 日付ごとにセッションをグルーピング: { "YYYY-MM-DD": [session1, session2], ... } */
function groupSessionsByDate(sessions) {
  const grouped = {};
  sessions.forEach((session) => {
    // 例: "2023-09-02T12:34:56.789" -> "2023-09-02"
    const dateStr = session.created_at?.split('T')[0];
    if (!grouped[dateStr]) {
      grouped[dateStr] = [];
    }
    grouped[dateStr].push(session);
  });
  return grouped;
}

function ChatHistoryManagement() {
  const theme = useTheme();
  const userMessageStyle = getUserMessageStyle(theme);
  const assistantMessageStyle = getAssistantMessageStyle(theme);
  const [users, setUsers] = useState([]);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_API_URL;

  // 「ユーザーごとの選択中の日付」を管理するためのオブジェクト。
  //  例: { [userId]: "2023-09-02", ... }
  const [selectedDateByUser, setSelectedDateByUser] = useState({});

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/chat_management/users`);
      setUsers(res.data);
    } catch (err) {
      console.error(err);
      alert('ユーザー一覧を取得できませんでした');
    }
  };

  // ユーザー行の折りたたみを切り替え
  const handleExpandClick = (userId) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
    } else {
      setExpandedUserId(userId);
    }
  };

  // ユーザーごとの日付選択を変更するハンドラ
  const handleDateChange = (userId, date) => {
    setSelectedDateByUser((prev) => ({
      ...prev,
      [userId]: date,
    }));
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
        チャット履歴管理
      </Typography>

      <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 1, mb: 2 }}>
        <CardContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            ユーザーごとのチャット履歴を閲覧できます。（日付を選択）
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ユーザーID</TableCell>
                  <TableCell>メールアドレス</TableCell>
                  <TableCell>ロール</TableCell>
                  <TableCell>チャット履歴</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => {
                  const isExpanded = expandedUserId === user.id;
                  return (
                    <React.Fragment key={user.id}>
                      <TableRow>
                        <TableCell>{user.id}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.role}</TableCell>
                        <TableCell>
                          <IconButton onClick={() => handleExpandClick(user.id)}>
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </TableCell>
                      </TableRow>

                      {/* 折りたたみ表示部分 */}
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2 }}>
                              {/* ユーザーのセッションを日付ごとにグルーピング */}
                              {user.ai_chat_sessions && user.ai_chat_sessions.length > 0 ? (
                                (() => {
                                  // グルーピングとソート
                                  const grouped = groupSessionsByDate(user.ai_chat_sessions);
                                  const dateKeys = Object.keys(grouped).sort(
                                    (a, b) => new Date(a) - new Date(b)
                                  );

                                  // ユーザーに対応する選択日付
                                  const currentSelectedDate = selectedDateByUser[user.id] || '';

                                  // 初回に選択がなければdateKeys[0]を選びたい場合は、 
                                  // ここで初期値を設定するロジックを組み込んでもOK (例: useEffect など)
                                  // 今回はユーザーがSelectで選択する想定

                                  if (dateKeys.length === 0) {
                                    return (
                                      <Typography variant="body2">
                                        チャットセッションなし
                                      </Typography>
                                    );
                                  }

                                  return (
                                    <Box>
                                      {/* 日付を選択するセレクト */}
                                      <FormControl sx={{ minWidth: 200, mb: 2 }}>
                                        <InputLabel id={`date-select-label-${user.id}`}>
                                          日付を選択
                                        </InputLabel>
                                        <Select
                                          labelId={`date-select-label-${user.id}`}
                                          label="日付を選択"
                                          value={currentSelectedDate}
                                          onChange={(e) =>
                                            handleDateChange(user.id, e.target.value)
                                          }
                                        >
                                          {/* 先頭に「未選択」オプションを入れる場合は↓ */}
                                          <MenuItem value="">未選択</MenuItem>
                                          {dateKeys.map((date) => (
                                            <MenuItem key={date} value={date}>
                                              {date}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>

                                      {/* 選択された日付のセッションのみ表示 */}
                                      {currentSelectedDate ? (
                                        grouped[currentSelectedDate].map((session) => (
                                          <Card
                                            key={session.id}
                                            variant="outlined"
                                            sx={{
                                              mb: 3,
                                              borderRadius: 2,
                                              boxShadow: 1,
                                            }}
                                          >
                                            <CardContent>
                                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                <strong>セッションID:</strong> {session.id}
                                                &nbsp;/&nbsp;
                                                <strong>作成日時:</strong> {session.created_at}
                                              </Typography>

                                              {session.messages && session.messages.length > 0 ? (
                                                <Box
                                                  sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 1,
                                                    border: `1px solid ${border(theme).default}`,
                                                    p: 2,
                                                    borderRadius: 2,
                                                  }}
                                                >
                                                  {session.messages.map((msg, idx) => {
                                                    const isUser = msg.role === 'user';
                                                    return (
                                                      <Box
                                                        key={idx}
                                                        sx={
                                                          isUser
                                                            ? userMessageStyle
                                                            : assistantMessageStyle
                                                        }
                                                      >
                                                        <Typography
                                                          variant="body2"
                                                          sx={{ fontWeight: 'bold', mb: 0.5 }}
                                                        >
                                                          {isUser ? 'ユーザー' : 'AI'}
                                                        </Typography>
                                                        <Typography
                                                          variant="body2"
                                                          sx={{ lineHeight: 1.5 }}
                                                        >
                                                          {msg.content}
                                                        </Typography>
                                                      </Box>
                                                    );
                                                  })}
                                                </Box>
                                              ) : (
                                                <Typography
                                                  variant="body2"
                                                  color="text.secondary"
                                                >
                                                  チャットメッセージなし
                                                </Typography>
                                              )}
                                            </CardContent>
                                          </Card>
                                        ))
                                      ) : (
                                        <Typography variant="body2">
                                          日付を選択してください
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })()
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  チャットセッションなし
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}

export default ChatHistoryManagement;